import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
// PrimeReact
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
// Components
import Panel from "../../../components/panel/panel";
import Select from "../../../components/input/Select";
// Services
import { getAccount } from "../../GlobalRoles/getAccount";
import { syncBankInstallments } from "./banksSyncService";
// Utils
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatIsoDate } from "../../../utils/formatDate";
// Context
import { useAppContext } from "../../../context/AppContext";
// Export Libraries
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";

export default function BanksSync() {
  const { permissions } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [syncData, setSyncData] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const form = useForm();
  const errors = form.formState.errors;

  // Buscar contas disponíveis
  const { data: accounts, isLoading: isLoadingAccounts } = useQuery({
    queryKey: ["accounts"],
    queryFn: () => getAccount(),
    enabled: !!permissions?.includes("145"), // Ajuste a permissão conforme necessário
  });

  // Sincronizar parcelas com o banco
  const handleSyncBank = async (accountId) => {
    try {
      // Chama o service real
      const result = await syncBankInstallments(accountId);
      return result;
    } catch (error) {
      // Se a API falhar, usa dados mock para demonstração
      console.warn("API não disponível, usando dados mock:", error);

      // Simula delay da API
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Dados simulados de retorno da sincronização
      const mockSyncData = [
        {
          id: 1,
          installment_number: "001/2024",
          client_name: "João Silva Santos",
          enterprise_name: "Residencial Vila Nova",
          block: "A",
          land: "15",
          due_date: "2024-01-15",
          original_value: 1500.00,
          updated_value: 1650.00,
          payment_status: "Pago",
          payment_date: "2024-01-14",
          sync_status: "Atualizado",
          sync_date: new Date().toISOString(),
        },
        {
          id: 2,
          installment_number: "002/2024",
          client_name: "Maria Oliveira Costa",
          enterprise_name: "Residencial Vila Nova",
          block: "B",
          land: "22",
          due_date: "2024-02-15",
          original_value: 1500.00,
          updated_value: 1500.00,
          payment_status: "Em Aberto",
          payment_date: null,
          sync_status: "Sincronizado",
          sync_date: new Date().toISOString(),
        },
        {
          id: 3,
          installment_number: "003/2024",
          client_name: "Carlos Roberto Lima",
          enterprise_name: "Residencial Vila Nova",
          block: "C",
          land: "08",
          due_date: "2024-03-15",
          original_value: 1800.00,
          updated_value: 1950.00,
          payment_status: "Pago",
          payment_date: "2024-03-10",
          sync_status: "Atualizado",
          sync_date: new Date().toISOString(),
        }
      ];

      return mockSyncData;
    }
  };

  const onSubmit = async (data) => {
    if (!data.account_id) {
      toast.warning("Selecione uma conta para sincronizar!");
      return;
    }

    setLoading(true);
    const notification = toast.loading("Sincronizando parcelas com o banco...");

    try {
      const result = await handleSyncBank(data.account_id);

      setSyncData(result);
      setTotalRecords(result.length);
      setShowTable(true);

      toast.update(notification, {
        render: `Sincronização concluída! ${result.length} parcelas processadas.`,
        autoClose: 3000,
        type: "success",
        isLoading: false,
      });
    } catch (error) {
      console.error("Erro na sincronização:", error);
      toast.update(notification, {
        render: "Erro ao sincronizar parcelas. Tente novamente.",
        autoClose: 5000,
        type: "error",
        isLoading: false,
      });
    } finally {
      setLoading(false);
    }
  };

  // Template para status de pagamento
  const paymentStatusTemplate = (rowData) => {
    const getStatusClass = (status) => {
      switch (status) {
        case "Pago":
          return "badge bg-success";
        case "Em Aberto":
          return "badge bg-warning";
        case "Vencido":
          return "badge bg-danger";
        default:
          return "badge bg-secondary";
      }
    };

    return (
      <span className={getStatusClass(rowData.payment_status)} style={{ fontSize: "10px" }}>
        {rowData.payment_status}
      </span>
    );
  };

  // Template para status de sincronização
  const syncStatusTemplate = (rowData) => {
    const getStatusClass = (status) => {
      switch (status) {
        case "Atualizado":
          return "badge bg-info";
        case "Sincronizado":
          return "badge bg-success";
        case "Erro":
          return "badge bg-danger";
        default:
          return "badge bg-secondary";
      }
    };

    return (
      <span className={getStatusClass(rowData.sync_status)} style={{ fontSize: "10px" }}>
        {rowData.sync_status}
      </span>
    );
  };

  // Template para valores monetários
  const valueTemplate = (rowData, field) => {
    return formatCurrency(rowData[field]);
  };

  // Template para datas
  const dateTemplate = (rowData, field) => {
    return rowData[field] ? formatIsoDate(rowData[field]) : "-";
  };

  // Função para exportar PDF
  const handleExportPDF = () => {
    const element = document.getElementById("banks-sync-report");
    if (!element) return;

    const options = {
      margin: 10,
      filename: "sincronizacao-bancaria.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
    };

    // Add force-light class for better PDF rendering
    element.classList.add("force-light");

    html2pdf()
      .from(element)
      .set(options)
      .save()
      .then(() => {
        element.classList.remove("force-light");
        toast.success("PDF exportado com sucesso!");
      })
      .catch((error) => {
        element.classList.remove("force-light");
        console.error("Erro ao exportar PDF:", error);
        toast.error("Erro ao exportar PDF!");
      });
  };

  // Função para exportar Excel
  const handleExportExcel = () => {
    if (!syncData || syncData.length === 0) {
      toast.warning("Nenhum dado disponível para exportação!");
      return;
    }

    const excelData = syncData.map((item) => ({
      "Nº Parcela": item.installment_number,
      "Cliente": item.client_name,
      "Empreendimento": item.enterprise_name,
      "Quadra": item.block,
      "Lote": item.land,
      "Vencimento": item.due_date ? formatIsoDate(item.due_date) : "-",
      "Valor Original": formatCurrency(item.original_value),
      "Valor Atualizado": formatCurrency(item.updated_value),
      "Status Pagamento": item.payment_status,
      "Data Pagamento": item.payment_date ? formatIsoDate(item.payment_date) : "-",
      "Status Sync": item.sync_status,
      "Data Sync": item.sync_date ? formatIsoDate(item.sync_date) : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sincronização Bancária");

    // Auto-adjust column widths
    const colWidths = [];
    Object.keys(excelData[0]).forEach((key, i) => {
      const maxLength = Math.max(
        key.length,
        ...excelData.map(row => String(row[key]).length)
      );
      colWidths[i] = { wch: Math.min(maxLength + 2, 50) };
    });
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, "sincronizacao-bancaria.xlsx");
    toast.success("Excel exportado com sucesso!");
  };

  return (
    <div className="row">
      {/* Filtros */}
      <div className="col-12 mb-3">
        <Panel title="Sincronização Bancária">
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="row align-items-end">
              <div className="col-xl-8 col-md-8 mb-3">
                <Select
                  name="account_id"
                  label={isLoadingAccounts ? "Carregando..." : "Conta Bancária*"}
                  control={form.control}
                  errors={errors}
                  isRequired={true}
                  disabled={isLoadingAccounts || loading}
                  options={
                    accounts &&
                    accounts.map((account) => ({
                      value: account.id,
                      label: `${account.nameCompany} | ${account.name_bank} | Conta: ${account.account_number} | Agência: ${account.agency}`,
                    }))
                  }
                />
              </div>
              <div className="col-xl-4 col-md-4 mb-3">
                <Button
                  type="submit"
                  label="Sincronizar"
                  icon="pi pi-sync"
                  loading={loading}
                  className="w-100"
                  disabled={isLoadingAccounts}
                />
              </div>
            </div>
          </form>
        </Panel>
      </div>

      {/* Tabela de Resultados */}
      {showTable && (
        <div className="col-12">
          <Panel
            title={`Resultado da Sincronização - ${totalRecords} parcelas processadas`}
            buttonPlanilha={
              <div className="d-flex align-items-center gap-2">
                <Button
                  icon="fa fa-file-pdf"
                  className="p-button-secondary cursor-pointer"
                  rounded
                  onClick={handleExportPDF}
                  tooltip="Exportar PDF"
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  icon="fa fa-file-excel"
                  className="p-button-success cursor-pointer"
                  rounded
                  onClick={handleExportExcel}
                  tooltip="Exportar Excel"
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
            }
          >
            <>
              <hr className="my-3" />
              <div className="px-2 pb-3" id="banks-sync-report">
                <DataTable
                  value={syncData}
                  rows={25}
                  paginator
                  stripedRows
                  showGridlines
                  // sortMode="multiple"
                  // globalFilter={globalFilterValue}
                  rowsPerPageOptions={[25, 50, 100]}
                  emptyMessage={
                    <div style={{ textAlign: "center" }}>
                      Nenhuma parcela foi processada na sincronização.
                    </div>
                  }
                  tableStyle={{ minWidth: "0.8rem", fontSize: "0.75rem" }}
                >
                  <Column
                    field="installment_number"
                    header="Nº Parcela"
                    sortable
                    style={{ minWidth: "120px" }}
                  />
                  <Column
                    field="client_name"
                    header="Cliente"
                    sortable
                    style={{ minWidth: "200px" }}
                  />
                  <Column
                    field="enterprise_name"
                    header="Empreendimento"
                    sortable
                    style={{ minWidth: "150px" }}
                  />
                  <Column
                    field="block"
                    header="Quadra"
                    sortable
                    style={{ minWidth: "80px" }}
                  />
                  <Column
                    field="land"
                    header="Lote"
                    sortable
                    style={{ minWidth: "80px" }}
                  />
                  <Column
                    field="due_date"
                    header="Vencimento"
                    sortable
                    body={(rowData) => dateTemplate(rowData, "due_date")}
                    style={{ minWidth: "120px" }}
                  />
                  <Column
                    field="original_value"
                    header="Valor Original"
                    sortable
                    body={(rowData) => valueTemplate(rowData, "original_value")}
                    style={{ minWidth: "130px" }}
                  />
                  <Column
                    field="updated_value"
                    header="Valor Atualizado"
                    sortable
                    body={(rowData) => valueTemplate(rowData, "updated_value")}
                    style={{ minWidth: "140px" }}
                  />
                  <Column
                    field="payment_status"
                    header="Status Pagamento"
                    sortable
                    body={paymentStatusTemplate}
                    style={{ minWidth: "130px" }}
                  />
                  <Column
                    field="payment_date"
                    header="Data Pagamento"
                    sortable
                    body={(rowData) => dateTemplate(rowData, "payment_date")}
                    style={{ minWidth: "130px" }}
                  />
                  <Column
                    field="sync_status"
                    header="Status Sync"
                    sortable
                    body={syncStatusTemplate}
                    style={{ minWidth: "120px" }}
                  />
                  <Column
                    field="sync_date"
                    header="Data Sync"
                    sortable
                    body={(rowData) => dateTemplate(rowData, "sync_date")}
                    style={{ minWidth: "130px" }}
                  />
                </DataTable>
              </div>
            </>
          </Panel>
        </div>
      )}

      {/* CSS para melhorar a aparência do PDF */}
      <style jsx>{`
        .force-light {
          background-color: white !important;
          color: black !important;
        }
        .force-light .p-datatable {
          background-color: white !important;
        }
        .force-light .p-datatable-thead > tr > th {
          background-color: #f8f9fa !important;
          color: black !important;
          border: 1px solid #dee2e6 !important;
        }
        .force-light .p-datatable-tbody > tr > td {
          background-color: white !important;
          color: black !important;
          border: 1px solid #dee2e6 !important;
        }
        .force-light .badge {
          color: white !important;
        }
      `}</style>
    </div>
  );
}